import React, { useEffect, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { toast } from 'react-toastify';

import { getConfig } from 'functions/getConfig';

const inretactWSURL = `${getConfig().WEBSOCKET_WS_URL}/freecode/interact`;

const FreecodePlayerInteract = ({
  humanInputProps,
  levelToRun,
  interactSessionState,
  restartInteractCommand,
  handleInteractResponse,
  startHumanInput,
  PixiComponent,
  updateInteractResult,
}) => {
  const interactSettingState = localStorage.getItem(`fsEditor-${humanInputProps.testId}-interactSettingState`);
  const resultRef = useRef(null);
  const stepsRef = useRef([]);

  const { sendJsonMessage, lastMessage } = useWebSocket(inretactWSURL, {
    onOpen: () => {
      initiateInteractWebSocket();
    },
    onClose: (event) => {
      onClose(event);
    },
    heartbeat: {
      message: 'ping',
      returnMessage: 'pong',
      timeout: 25000, // 25 seconds, if no response is received, the connection will be closed
      interval: 10000, // every 10 seconds, a ping message will be sent
    },
  });

  const initiateInteractWebSocket = () => {
    sendJsonMessage({
      type: 'request',
      ...humanInputProps,
      levelToRun,
      useInteractState: true,
      interactSessionState: interactSessionState,
      interactSettingState: interactSettingState,
    });

    PixiComponent.setCommandHandler((command) => {
      sendJsonMessage(command);
    });
  };

  const onInteractResponse = (error, result, steps, interactSessionState) => {
    let stopInteracting = false;
    let pipe = 'out';
    let message = 'Waiting for user input...';

    if (error || result) {
      PixiComponent.setCommandHandler(null);
      stopInteracting = true;

      if (result) interactSessionState = null;

      if (updateInteractResult) updateInteractResult(result && result?.maxScore === result?.scores?.[0]);

      if (error) {
        resultRef.current = error;
        message = error;
        pipe = 'err';
      } else message = '';

      if (interactSessionState) {
        PixiComponent.setRestartCommandHandler((command) => {
          startHumanInput(command);
        });
        message += '\nTo continue click on resume interact.';
      } else PixiComponent.setRestartCommandHandler(null);
    }

    const logEntry = { tag: null, index: null, pipe: pipe, message: message };

    const logSteps = [{ log: [logEntry], graphics: [] }];

    handleInteractResponse([...steps, ...logSteps], stopInteracting, interactSessionState);
  };

  const onClose = (event) => {
    if (resultRef.current) return;
    if (event?.reason !== '') toast.error(event.reason); // internal error
    const error = 'Connection lost or terminated by user';
    onInteractResponse(error, resultRef.current, stepsRef.current, interactSessionState);
  };

  const onResponse = async (data) => {
    if (data instanceof Blob) data = await data.text();
    const message = JSON.parse(data);

    let error = null;
    let newInteractSessionState = null;

    if (message instanceof Array) {
      stepsRef.current.push(...message);
    } else if (message.steps instanceof Array) {
      stepsRef.current.push(...message.steps);
      newInteractSessionState = message.interactSessionState;
      if (message.interactSettingState) {
        localStorage.setItem(`fsEditor-${humanInputProps.testId}-interactSettingState`, message.interactSettingState);
      } else localStorage.removeItem(`fsEditor-${humanInputProps.testId}-interactSettingState`);
    } else if (message.serverError) {
      if (message.serverError.message) error = message.serverError.message;
      else error = message.serverError;
    } else if (message.levelResult) {
      stepsRef.current.push(...message.levelOutput);
      resultRef.current = message.levelResult;
    } else error = 'Unknown response';

    if (restartInteractCommand) {
      sendJsonMessage(restartInteractCommand);
      PixiComponent.setRestartCommandHandler(null);
    }
    onInteractResponse(error, resultRef.current, stepsRef.current, newInteractSessionState);
  };

  useEffect(
    () => {
      if (lastMessage && lastMessage.data) onResponse(lastMessage.data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastMessage]
  );
};

export default React.memo(FreecodePlayerInteract);
